import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import ImageUploader from '../../Common/ImageUploader';
import { createBlogPost, updateBlogPost, uploadImage } from '../../../services/CommonServices';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Modal Overlay - Full Screen
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: #1a1a1a;
  border-radius: 8px;
  width: 100%;
  max-width: 1400px;
  height: 95vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 24px;
  border-bottom: 1px solid #444;
  background-color: #222;
  flex-shrink: 0;
`;

const ModalTitle = styled.h5`
  margin: 0;
  color: #fff;
  font-size: 1.1rem;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Button = styled.button`
  background-color: ${(props) => {
    if (props.variant === 'secondary') return '#6c757d';
    if (props.variant === 'danger') return '#dc3545';
    return '#28a745';
  }};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => {
      if (props.variant === 'secondary') return '#5a6268';
      if (props.variant === 'danger') return '#bd2130';
      return '#218838';
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #888;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  margin-left: 10px;

  &:hover {
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

// Left Panel - Metadata
const LeftPanel = styled.div`
  width: 320px;
  background-color: #252525;
  border-right: 1px solid #444;
  padding: 20px;
  overflow-y: auto;
  flex-shrink: 0;
`;

// Right Panel - Content Editor
const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditorHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #444;
  background-color: #222;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Editor Content with Blog Post matching styles
const EditorContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background-color: #fff;

  /* Quill container styles */
  .ql-toolbar {
    background-color: #f8f9fa;
    border: none;
    border-bottom: 1px solid #e0e0e0;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .ql-container {
    border: none;
    font-family: inherit;
  }

  /* Match BlogPost Content styles exactly */
  .ql-editor {
    max-width: 800px;
    margin: 0 auto;
    padding: 40px 20px 60px;
    line-height: 1.9;
    font-size: 1.1rem;
    color: #333;
    min-height: 100%;

    img {
      max-width: 100%;
      height: auto;
      margin: 28px 0;
      border-radius: 8px;
      display: block;
    }

    h1 {
      font-size: 2.2rem;
      font-weight: 700;
      line-height: 1.4;
      margin-top: 48px;
      margin-bottom: 20px;
      color: #222;
    }

    h2 {
      font-size: 1.6rem;
      font-weight: 600;
      margin-top: 48px;
      margin-bottom: 20px;
      color: #222;
    }

    h3 {
      font-size: 1.3rem;
      font-weight: 600;
      margin-top: 36px;
      margin-bottom: 16px;
      color: #333;
    }

    h4 {
      font-size: 1.15rem;
      font-weight: 600;
      margin-top: 28px;
      margin-bottom: 12px;
      color: #333;
    }

    p {
      margin-bottom: 24px;
    }

    ul, ol {
      margin-bottom: 24px;
      padding-left: 24px;

      li {
        margin-bottom: 8px;
      }
    }

    blockquote {
      border-left: 4px solid #c41e3a;
      margin: 28px 0;
      padding: 16px 24px;
      background: #f9f9f9;
      border-radius: 0 8px 8px 0;
      font-style: italic;
      color: #555;
    }

    a {
      color: #c41e3a;
      text-decoration: underline;
      text-underline-offset: 2px;

      &:hover {
        opacity: 0.8;
      }
    }

    pre.ql-syntax {
      background-color: #1e1e1e;
      color: #d4d4d4;
      padding: 16px 20px;
      border-radius: 8px;
      overflow-x: auto;
      font-family: 'Consolas', 'Monaco', monospace;
      font-size: 0.95rem;
      line-height: 1.5;
      margin: 24px 0;
    }

    .ql-align-center {
      text-align: center;
    }

    .ql-align-right {
      text-align: right;
    }

    .ql-align-justify {
      text-align: justify;
    }
  }

  .ql-editor.ql-blank::before {
    color: #999;
    font-style: normal;
  }
`;

const HtmlEditor = styled.textarea`
  width: 100%;
  height: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  background-color: #1e1e1e;
  border: none;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.95rem;
  line-height: 1.6;
  resize: none;
  display: block;

  &:focus {
    outline: none;
  }
`;

const HtmlEditorWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: #1e1e1e;
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #aaa;
  font-size: 0.85rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background-color: #333;
  border: 1px solid #555;
  color: white;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #28a745;
  }

  &::placeholder {
    color: #666;
  }
`;

const ModeButton = styled.button`
  padding: 6px 14px;
  font-size: 0.8rem;
  border: 1px solid ${props => props.active ? '#28a745' : '#555'};
  background-color: ${props => props.active ? '#28a745' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#aaa'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #28a745;
    color: #fff;
  }
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #555;
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background-color: #28a745;
  }

  input:checked + span:before {
    transform: translateX(20px);
  }
`;

const SwitchText = styled.span`
  font-size: 0.85rem;
  color: ${props => props.active ? '#28a745' : '#888'};
`;

const ScheduleWrapper = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #444;
`;

const ScheduleCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #aaa;
  font-size: 0.85rem;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const DateTimeInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background-color: #333;
  border: 1px solid #555;
  color: white;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 10px;

  &:focus {
    outline: none;
    border-color: #f0ad4e;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScheduleInfo = styled.div`
  margin-top: 8px;
  font-size: 0.75rem;
  color: #f0ad4e;
`;

const SectionDivider = styled.div`
  border-top: 1px solid #444;
  margin: 20px 0;
  padding-top: 20px;
`;

const SectionLabel = styled.div`
  color: #888;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
`;

const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1.2rem;
  z-index: 100;
`;

const PostEditor = ({ siteId, post, onCancel, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    thumbnail: '',
    content: '',
    isPublished: false,
    scheduledAt: '',
  });
  const [useSchedule, setUseSchedule] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editorMode, setEditorMode] = useState('wysiwyg');
  const quillRef = useRef(null);

  useEffect(() => {
    if (post) {
      const hasSchedule = !!post.scheduled_at;
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        thumbnail: post.thumbnail || '',
        content: post.content || '',
        isPublished: post.is_published === 1 || post.is_published === true,
        scheduledAt: post.scheduled_at ? formatDateTimeLocal(post.scheduled_at) : '',
      });
      setUseSchedule(hasSchedule);
    }
  }, [post]);

  // Format datetime to datetime-local input format (YYYY-MM-DDTHH:mm)
  const formatDateTimeLocal = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const generateRandomId = () => {
    return `post-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'title' && !post) {
        newData.slug = generateSlug(value);
      }
      return newData;
    });
  };

  const handleContentChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  // Upload image to Cloudflare R2
  const uploadToR2 = async (file) => {
    setIsUploading(true);
    try {
      const response = await uploadImage(file, 'blog-content');
      if (response.data.success) {
        return response.data.data.url;
      } else {
        alert('Image upload failed: ' + response.data.message);
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Image upload failed');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Custom image handler for Quill
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        const url = await uploadToR2(file);
        if (url && quillRef.current) {
          const editor = quillRef.current.getEditor();
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1);
        }
      }
    };
  };

  // Quill modules with custom image handler
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false,
    }
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  // Handle paste event for images
  useEffect(() => {
    if (editorMode !== 'wysiwyg' || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const handlePaste = async (e) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          e.stopPropagation();

          const file = items[i].getAsFile();
          if (file) {
            const url = await uploadToR2(file);
            if (url) {
              const range = editor.getSelection(true);
              editor.insertEmbed(range.index, 'image', url);
              editor.setSelection(range.index + 1);
            }
          }
          break;
        }
      }
    };

    const editorElement = editor.root;
    editorElement.addEventListener('paste', handlePaste);

    return () => {
      editorElement.removeEventListener('paste', handlePaste);
    };
  }, [editorMode]);

  // Handle drop event for images
  useEffect(() => {
    if (editorMode !== 'wysiwyg' || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const handleDrop = async (e) => {
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (file.type.indexOf('image') !== -1) {
        e.preventDefault();
        e.stopPropagation();

        const url = await uploadToR2(file);
        if (url) {
          const range = editor.getSelection(true) || { index: editor.getLength() };
          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1);
        }
      }
    };

    const editorElement = editor.root;
    editorElement.addEventListener('drop', handleDrop);

    return () => {
      editorElement.removeEventListener('drop', handleDrop);
    };
  }, [editorMode]);

  const handleSubmit = async () => {
    if (!formData.title) {
      alert('Title is required');
      return;
    }

    const finalSlug = formData.slug.trim() || generateRandomId();
    const submitData = { ...formData, slug: finalSlug };

    setIsSaving(true);
    try {
      if (post) {
        await updateBlogPost(post.id, submitData);
      } else {
        await createBlogPost({ siteId, ...submitData });
      }
      onSave();
    } catch (err) {
      console.error(err);
      alert('Error saving post');
    } finally {
      setIsSaving(false);
    }
  };

  // Render as portal to body for full-screen modal
  return ReactDOM.createPortal(
    <ModalOverlay onClick={onCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {post ? 'Edit Post' : 'New Post'}
          </ModalTitle>
          <HeaderButtons>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || isUploading}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <CloseButton onClick={onCancel}>
              &times;
            </CloseButton>
          </HeaderButtons>
        </ModalHeader>

        <ModalBody>
          {/* Left Panel - Metadata */}
          <LeftPanel>
            <SectionLabel>Post Details</SectionLabel>

            <FormGroup>
              <Label>Title *</Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter post title"
              />
            </FormGroup>

            <FormGroup>
              <Label>Slug (URL)</Label>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="auto-generated-from-title"
              />
            </FormGroup>

            <SectionDivider />
            <SectionLabel>Thumbnail</SectionLabel>

            <FormGroup>
              <ImageUploader
                initialUrl={formData.thumbnail}
                onUploadSuccess={(url) => setFormData({ ...formData, thumbnail: url })}
                folder="blog-assets"
              />
            </FormGroup>

            <SectionDivider />
            <SectionLabel>Publishing</SectionLabel>

            <FormGroup>
              <SwitchWrapper>
                <Switch>
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                  />
                  <span />
                </Switch>
                <SwitchText active={formData.isPublished}>
                  {formData.isPublished ? 'Published' : 'Draft'}
                </SwitchText>
              </SwitchWrapper>

              {formData.isPublished && (
                <ScheduleWrapper>
                  <ScheduleCheckbox>
                    <input
                      type="checkbox"
                      checked={useSchedule}
                      onChange={(e) => {
                        setUseSchedule(e.target.checked);
                        if (!e.target.checked) {
                          setFormData({ ...formData, scheduledAt: '' });
                        }
                      }}
                    />
                    Schedule publish date
                  </ScheduleCheckbox>
                  {useSchedule && (
                    <>
                      <DateTimeInput
                        type="datetime-local"
                        value={formData.scheduledAt}
                        onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                      />
                      <ScheduleInfo>
                        Post will be visible to visitors after this date
                      </ScheduleInfo>
                    </>
                  )}
                </ScheduleWrapper>
              )}
            </FormGroup>
          </LeftPanel>

          {/* Right Panel - Content Editor */}
          <RightPanel>
            <EditorHeader>
              <ModeButton
                active={editorMode === 'wysiwyg'}
                onClick={() => setEditorMode('wysiwyg')}
              >
                WYSIWYG
              </ModeButton>
              <ModeButton
                active={editorMode === 'html'}
                onClick={() => setEditorMode('html')}
              >
                HTML
              </ModeButton>
              {isUploading && (
                <span style={{ color: '#888', fontSize: '0.85rem', marginLeft: 'auto' }}>
                  Uploading image...
                </span>
              )}
            </EditorHeader>

            {editorMode === 'wysiwyg' ? (
              <EditorContent>
                {isUploading && (
                  <UploadingOverlay>
                    Uploading image...
                  </UploadingOverlay>
                )}
                <ReactQuill
                  ref={quillRef}
                  value={formData.content}
                  onChange={handleContentChange}
                  theme="snow"
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your blog content here..."
                  style={{ height: '100%' }}
                />
              </EditorContent>
            ) : (
              <HtmlEditorWrapper>
                <HtmlEditor
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  placeholder="Enter HTML code directly..."
                />
              </HtmlEditorWrapper>
            )}
          </RightPanel>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default PostEditor;
